import React, { useEffect, useState } from 'react';
import Messages from '../messages/Messages';
import { Message } from '../messages/Message';
import PopoutDrawer from '../../popout-drawer/PopoutDrawer';
import { Accordion } from '@jbhi-fi/lanyard-ui/components/Accordion';
import { styles } from './ProductConditionInfo.css';
import NegativeIcon from '@jbhi-fi/lanyard-ui/components/Icons/Negative';
import PositiveIcon from '@jbhi-fi/lanyard-ui/components/Icons/Positive';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { ImageModal } from '../images/modal/ImageModal';

const RENEWED_CONDITION_CODE = '99';
export interface Condition {
  name: string;
  conditionCode?: string;
  messageDescription?: string;
  modalDescription?: string;
  modalImages?: Array<string>;
}

export type ProductConditionInfoProps = {
  info: {
    messageHeading: string;
    modalHeading: string;
    modalOverview: string;
    conditions: Array<Condition>;
  };
  classNames: string;
  productConditionIndex: number;
};

const ProductConditionInfo = ({ info, classNames: className, productConditionIndex }: ProductConditionInfoProps) => {
  const { messageHeading, modalHeading, modalOverview, conditions } = info;
  const [showProductConditionInfo, setShowProductConditionInfo] = useState(false);
  const [refurbishedInfo, setRefurbishedInfo] = useState<{ condition: Message }>({
    condition: {
      MessageType: 'General',
      MessageHeading: '',
      Message: '',
      handleClick: () => {},
    },
  });
  const [accordionState, setAccordionState] = useState<Array<number>>([productConditionIndex]);
  const [currentModalImageIndex, setCurrentModalImageIndex] = useState<number>(0);
  const [currentModalCondition, setCurrentModalCondition] = useState<Condition | undefined>(undefined);

  const modalImages = currentModalCondition?.modalImages;
  const modalTitle = currentModalCondition?.name;

  const handleImageClick = (condition: Condition, imageIndex: number) => {
    setCurrentModalCondition(condition);
    setCurrentModalImageIndex(imageIndex);
  };

  const handleModalClose = () => {
    setCurrentModalCondition(undefined);
    setCurrentModalImageIndex(0);
  };

  useEffect(() => {
    if (productConditionIndex < 0) return;
    setRefurbishedInfo({
      condition: {
        MessageType: 'General',
        MessageHeading: `${messageHeading}\u02DC`,
        Message: conditions[productConditionIndex]?.messageDescription || '',
        handleClick: handleMoreInfoClick,
      },
    });
  }, [productConditionIndex]);

  useEffect(() => {
    if (showProductConditionInfo) {
      setAccordionState([productConditionIndex]);
    }
  }, [showProductConditionInfo, productConditionIndex]);

  const handleMoreInfoClick = () => {
    setShowProductConditionInfo(!showProductConditionInfo);
  };

  const getProductConditionInfo = (): Array<Message> => {
    return refurbishedInfo?.condition.Message ? [refurbishedInfo.condition] : [];
  };
  const getAccordionItems = () => {
    return conditions
      .filter(condition => condition.conditionCode !== RENEWED_CONDITION_CODE)
      .map((condition: Condition) => {
        return {
          urlHashId: condition.name,
          head: <span className={styles.conditionAccordionTitle}>{condition.name}</span>,
          content: (
            <span className="">
              {condition.modalDescription}
              {condition.modalImages ? (
                <div>
                  <span className={styles.conditionAccordionImageTitle}>Example images only</span>
                  <div className={styles.conditionAccordionImagesGrid}>
                    {condition.modalImages.map((image, i) => (
                      <button
                        key={`image-${i}`}
                        className={styles.conditionImageButton}
                        onClick={() => {
                          handleImageClick(condition, i);
                        }}
                      >
                        <img
                          className={styles.conditionAccordionImage}
                          src={image}
                          alt={`${condition.name} ${i + 1}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </span>
          ),
          closeIcon: <NegativeIcon />,
          openIcon: <PositiveIcon />,
        };
      });
  };

  const onAccordionChange = (activeIndex: Array<number>) => {
    if (activeIndex?.length && accordionState?.includes(activeIndex[0])) {
      setAccordionState([]);
    } else {
      setAccordionState(activeIndex);
    }
  };

  if (conditions?.length === 0) return null;

  return (
    <div>
      <Messages
        messages={getProductConditionInfo()}
        className={classNames([styles.productConditionMessages, className])}
      />
      <PopoutDrawer anchor="right" isOpen={showProductConditionInfo} onClose={handleMoreInfoClick} title={modalHeading}>
        <div>
          <p>{modalOverview}</p>
          <Accordion
            id="pdp-product-condition-accordion"
            data-testid="pdp-product-condition-accordion"
            className={styles.conditionAccordion}
            accordionItemClassname={styles.conditionAccordionItem}
            accordionHeaderClassname={styles.conditionAccordionHead}
            accordionContentClassname={styles.conditionAccordionContent}
            items={getAccordionItems()}
            activeState={accordionState}
            onActiveStateChange={(active: Array<number>) => {
              onAccordionChange(active);
            }}
          />
        </div>
      </PopoutDrawer>
      {currentModalCondition ? (
        <ImageModal
          sortedImages={modalImages ?? []}
          title={modalTitle ?? ''}
          toggleModal={handleModalClose}
          showModal
          initialSlide={currentModalImageIndex}
        />
      ) : null}
    </div>
  );
};

export default ProductConditionInfo;
